import React, { useState, useEffect } from 'react';
import { FiUpload, FiEdit, FiTrash2, FiEye, FiPlay, FiX } from 'react-icons/fi';

interface BubbleVideo {
  id: string;
  title: string;
  video_url: string;
  thumbnail_url: string;
  duration: string;
  is_active: number;
  created_at: string;
}

const BubbleVideos: React.FC = () => {
  const [videos, setVideos] = useState<BubbleVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState<BubbleVideo | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    duration: '',
    is_active: 1
  });
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Base URL for your server
  const baseURL = 'https://mandilive.in/';
  
  // Helper function to get full URL
  const getFullURL = (path: string) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    return baseURL + path;
  };

  // Fetch videos from API
  const fetchVideos = async () => {
    try {
      setIsLoading(true);
      const timestamp = new Date().getTime();
      const response = await fetch(`${baseURL}api/bubble-videos?v=${timestamp}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setVideos([]);
          setError('Bubble videos API not deployed yet. Please deploy the API endpoint first.');
          return;
        }
        throw new Error('Failed to fetch videos');
      }
      
      const data = await response.json();
      setVideos(data.data || []);
      setError(null);
    } catch (error) {
      console.error('Error fetching videos:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('duration', formData.duration);
      formDataToSend.append('is_active', formData.is_active.toString());
      
      if (videoFile) {
        formDataToSend.append('video', videoFile);
      }
      
      const url = editingVideo 
        ? `${baseURL}api/bubble-videos/${editingVideo.id}`
        : `${baseURL}api/bubble-videos`;
      
      const method = editingVideo ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        body: formDataToSend,
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${editingVideo ? 'update' : 'upload'} video`);
      }
      
      // Reset form and refresh videos
      setFormData({ title: '', duration: '', is_active: 1 });
      setVideoFile(null);
      setShowUploadForm(false);
      setEditingVideo(null);
      await fetchVideos();
      
    } catch (error) {
      console.error('Upload error:', error);
      setError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Delete video
  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    try {
      const response = await fetch(`${baseURL}api/bubble-videos/${id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete video');
      }
      
      await fetchVideos();
    } catch (error) {
      console.error('Delete error:', error);
      setError(error instanceof Error ? error.message : 'Delete failed');
    }
  };

  // Set video as active
  const handleSetActive = async (id: string) => {
    try {
      const response = await fetch(`${baseURL}api/bubble-videos/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: 1 }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to set video as active');
      }
      
      await fetchVideos();
    } catch (error) {
      console.error('Set active error:', error);
      setError(error instanceof Error ? error.message : 'Failed to set active');
    }
  };

  // Start editing
  const handleEdit = (video: BubbleVideo) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      duration: video.duration,
      is_active: video.is_active
    });
    setShowUploadForm(true);
  };

  // Cancel editing
  const handleCancel = () => {
    setShowUploadForm(false);
    setEditingVideo(null);
    setFormData({ title: '', duration: '', is_active: 1 });
    setVideoFile(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Bubble Videos Management</h2>
            <p className="text-gray-600">Manage the floating video bubble that appears on your home page</p>
          </div>
          <button
            onClick={() => setShowUploadForm(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium flex items-center space-x-2 transition-colors"
          >
            <FiUpload className="w-5 h-5" />
            <span>Upload Video</span>
          </button>
        </div>
      </div>

      {/* Upload/Edit Form */}
      {showUploadForm && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {editingVideo ? 'Edit Video' : 'Upload New Video'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter video title in Hindi"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration
              </label>
              <input
                type="text"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="e.g., 3:45"
              />
            </div>

            {!editingVideo && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video File
                </label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>
            )}

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active === 1}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <label htmlFor="is_active" className="text-sm text-gray-700">
                Set as active video (will deactivate all other videos)
              </label>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={uploading}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                {uploading ? 'Processing...' : editingVideo ? 'Update Video' : 'Upload Video'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <FiX className="w-5 h-5 text-red-400 mt-0.5" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Videos List */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Uploaded Videos</h3>
        </div>
        
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading videos...</p>
          </div>
        ) : videos.length === 0 ? (
          <div className="p-8 text-center">
            <FiPlay className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No videos uploaded yet</p>
            <p className="text-sm text-gray-400 mt-2">Upload your first bubble video to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {videos.map((video) => (
              <div key={video.id} className="p-6 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Video Preview */}
                  <div className="w-24 h-16 bg-gray-100 rounded-lg overflow-hidden">
                    <video
                      className="w-full h-full object-cover"
                      preload="metadata"
                      muted
                      onLoadedData={(e) => {
                        e.currentTarget.currentTime = 0.1;
                      }}
                    >
                      <source src={getFullURL(video.video_url)} type="video/mp4" />
                    </video>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">{video.title}</h4>
                    <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                      <span>Duration: {video.duration || 'N/A'}</span>
                      <span>•</span>
                      <span>Created: {new Date(video.created_at).toLocaleDateString()}</span>
                      <span>•</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        video.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {video.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {/* <a
                    href={getFullURL(video.video_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="View Video"
                  >
                    <FiEye className="w-5 h-5" />
                  </a>
                  
                  {!video.is_active && (
                    <button
                      onClick={() => handleSetActive(video.id)}
                      className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Set as Active"
                    >
                      <FiPlay className="w-5 h-5" />
                    </button>
                  )}
                   */}
                  {/* <button
                    onClick={() => handleEdit(video)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit Video"
                  >
                    <FiEdit className="w-5 h-5" />
                  </button> */}
                  
                  <button
                    onClick={() => handleDelete(video.id)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete Video"
                  >
                    <FiTrash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BubbleVideos;
